import HeroSection from "./heroSection";
import { Col, Row, Space, Input } from "antd";
import { EnvironmentOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { Form, Button } from "antd";
import { useEffect, useState } from "react";

function Contact() {
  const [screenSize, setScreenSize] = useState("desktop");

  useEffect(() => {
    document.title = "Contact - Optix360";

    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setScreenSize("mobile");
      } else if (window.innerWidth <= 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    // Set initial screen size
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up event listener
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPaddingStyle = () => {
    switch (screenSize) {
      case "mobile":
        return { padding: "30px 15px" };
      case "tablet":
        return { padding: "50px 30px" };
      default:
        return { padding: "100px 110px 100px 110px" };
    }
  };

  const getMainRowGutter = () => {
    switch (screenSize) {
      case "mobile":
        return [0, 24];
      default:
        return 32;
    }
  };

  const getMainColSpan = () => {
    switch (screenSize) {
      case "mobile":
        return 24;
      default:
        return 12;
    }
  };

  const getContactRowGutter = () => {
    switch (screenSize) {
      case "mobile":
        return [0, 24];
      default:
        return 32;
    }
  };

  const getContactColStyle = () => {
    switch (screenSize) {
      case "mobile":
        return {
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        };
      default:
        return {
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          borderRight: "1px solid #e7e7e8",
          paddingRight: "16px",
        };
    }
  };

  const getSecondContactColStyle = () => {
    switch (screenSize) {
      case "mobile":
        return {
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        };
      default:
        return {
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        };
    }
  };

  const getBorderRightStyle = () => {
    switch (screenSize) {
      case "mobile":
        return { borderBottom: "1px solid #e7e7e8", paddingBottom: 20, marginBottom: 24 };
      default:
        return { borderRight: "1px solid #e7e7e8", paddingRight: "24px" };
    }
  };

  const getTitleStyle = () => {
    switch (screenSize) {
      case "mobile":
        return {
          fontSize: "24px",
          fontWeight: "bold",
          letterSpacing: "0.5px",
          lineHeight: "32px",
          color: "#0c0d0d",
          marginBottom: 12,
        };
      default:
        return {
          fontSize: "32px",
          fontWeight: "bold",
          letterSpacing: "0.5px",
          lineHeight: "42px",
          color: "#0c0d0d",
          marginBottom: 12,
        };
    }
  };

  const getSubtitleStyle = () => {
    switch (screenSize) {
      case "mobile":
        return {
          fontSize: "14px",
          lineHeight: "22px",
          color: "rgb(75, 85, 99)",
          marginBottom: 0,
        };
      default:
        return {
          fontSize: "16px",
          lineHeight: "26px",
          color: "rgb(75, 85, 99)",
          marginBottom: 0,
        };
    }
  };

  const getHeadingStyle = () => {
    switch (screenSize) {
      case "mobile":
        return {
          fontSize: "18px",
          fontWeight: "600",
          color: "#0c0d0d",
          marginBottom: 4,
        };
      default:
        return {
          fontSize: "20px",
          fontWeight: "600",
          color: "#0c0d0d",
          marginBottom: 4,
        };
    }
  };

  const getTextStyle = () => {
    switch (screenSize) {
      case "mobile":
        return { fontSize: "14px", color: "rgb(75, 85, 99)" };
      default:
        return { fontSize: "16px", color: "rgb(75, 85, 99)" };
    }
  };

  const getFormStyle = () => {
    switch (screenSize) {
      case "mobile":
        return { maxWidth: "100%", margin: "20px 0 0 0" };
      default:
        return { maxWidth: 600, margin: "20px 0 0 20px" };
    }
  };

  const getFormRowGutter = () => {
    switch (screenSize) {
      case "mobile":
        return 16;
      default:
        return 16;
    }
  };

  const getFormColSpan = () => {
    switch (screenSize) {
      case "mobile":
        return 24;
      default:
        return 12;
    }
  };

  return (
    <>
      <HeroSection />

      <div style={getPaddingStyle()}>
        <Row gutter={getMainRowGutter()}>
          <Col span={getMainColSpan()} style={getBorderRightStyle()}>
            <div style={{ borderBottom: "1px solid #e7e7e8", paddingBottom: 20, marginBottom: 24 }}>
              <h1 style={getTitleStyle()}>
                Let’s Grow Your Optical Business Together
              </h1>
              <p style={getSubtitleStyle()}>
                Have questions or need help with Optix360? We're here to support you every step of the way. Whether you're facing technical issues, need assistance with setup, or want to explore features like billing, inventory, or sales tracking — our team is ready to assist you.
              </p>
            </div>

            <Row gutter={getContactRowGutter()}>
              {/* Address */}
              <Col
                span={getMainColSpan()}
                style={getContactColStyle()}
              >
                <h2 style={getHeadingStyle()}>
                  Address
                </h2>
                <Space align="start">
                  <EnvironmentOutlined style={{ fontSize: "18px", color: "#1890ff", marginTop: "2px" }} />
                  <span style={getTextStyle()}>
                    I-14, Street 30, Islamabad, Pakistan
                  </span>
                </Space>
              </Col>

              {/* Contact */}
              <Col
                span={getMainColSpan()}
                style={getSecondContactColStyle()}
              >
                <h2 style={getHeadingStyle()}>
                  Contact
                </h2>

                <Space align="start">
                  <MailOutlined style={{ fontSize: "18px", color: "#1890ff", marginTop: "2px" }} />
                  <span style={getTextStyle()}>
                    info@optix360.com
                  </span>
                </Space>

                <Space align="start">
                  <PhoneOutlined style={{ fontSize: "18px", color: "#1890ff", marginTop: "2px" }} />
                  <span style={getTextStyle()}>
                    +92 307 5487676
                  </span>
                </Space>
              </Col>
            </Row>
          </Col>

          <Col span={getMainColSpan()} style={{ display: "flex", flexDirection: "column" }}>
            <h1 style={getTitleStyle()}>Contact Us</h1>
            <p style={getSubtitleStyle()}>
              Fill The Contact Form
              Feel free to contact with us, we don’t spam your email
            </p>

            <Form
              layout="vertical"
              style={getFormStyle()}
            >
              <Row gutter={getFormRowGutter()}>
                <Col span={getFormColSpan()}>
                  <Form.Item name="name" label="Name">
                    <Input placeholder="Enter your name" />
                  </Form.Item>
                </Col>
                <Col span={getFormColSpan()}>
                  <Form.Item name="phone" label="Phone">
                    <Input placeholder="Enter your phone" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="email" label="Email">
                <Input placeholder="Enter your email" />
              </Form.Item>

              <Form.Item name="message" label="Message">
                <Input.TextArea rows={3} placeholder="Enter your message" />
              </Form.Item>

              {/* Submit button centered */}
              <Form.Item>
                <div style={{ textAlign: "center" }}>
                  <Button type="primary" htmlType="submit" style={{ backgroundColor: "#000", borderColor: "#000" }}>
                    Send Message
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Contact;